import { useCallback, useEffect, useRef, useState } from "react";
import adminApi from "../api/adminApi";
import {
  EMPTY_ANALYTICS,
  EMPTY_FUNNEL_PERIOD,
  EMPTY_MONEY_PERIOD,
  EMPTY_PURCHASE_SERIES,
  EMPTY_REQUESTS_PERIOD
} from "../constants";
import { INITIAL_STATS } from "../utils/defaults";

function normalizeAnalytics(payload = {}) {
  const meta = payload.meta || {};
  const periods = payload.periods || {};
  const rollingPeriods = payload.rollingPeriods || {};
  const money = payload.money || {};
  const roles = payload.roles || {};
  const cohorts = payload.cohorts || {};
  const retention = payload.retention || {};
  const dropOff = payload.dropOff || {};
  const conclusions = payload.conclusions || {};
  const dashboard = payload.dashboard || {};
  const weekly = payload.weekly || {};
  const requests = payload.requests || {};

  return {
    ...EMPTY_ANALYTICS,
    ...payload,
    meta: {
      ...EMPTY_ANALYTICS.meta,
      ...meta,
      labels: {
        ...EMPTY_ANALYTICS.meta.labels,
        ...(meta.labels || {})
      }
    },
    periods: {
      day: { ...EMPTY_FUNNEL_PERIOD, ...(periods.day || {}) },
      week: { ...EMPTY_FUNNEL_PERIOD, ...(periods.week || {}) },
      month: { ...EMPTY_FUNNEL_PERIOD, ...(periods.month || {}) }
    },
    rollingPeriods: {
      day: { ...EMPTY_FUNNEL_PERIOD, ...(rollingPeriods.day || {}) },
      week: { ...EMPTY_FUNNEL_PERIOD, ...(rollingPeriods.week || {}) },
      month: { ...EMPTY_FUNNEL_PERIOD, ...(rollingPeriods.month || {}) }
    },
    money: {
      ...EMPTY_ANALYTICS.money,
      ...money,
      day: { ...EMPTY_MONEY_PERIOD, ...(money.day || {}) },
      week: { ...EMPTY_MONEY_PERIOD, ...(money.week || {}) },
      month: { ...EMPTY_MONEY_PERIOD, ...(money.month || {}) }
    },
    roles: {
      ...EMPTY_ANALYTICS.roles,
      ...roles
    },
    weekly: {
      ...EMPTY_ANALYTICS.weekly,
      ...weekly,
      purchaseTypes: {
        ...EMPTY_ANALYTICS.weekly.purchaseTypes,
        ...(weekly.purchaseTypes || {}),
        subscriptionNew: {
          ...EMPTY_ANALYTICS.weekly.purchaseTypes.subscriptionNew,
          ...(weekly.purchaseTypes?.subscriptionNew || {})
        },
        oneTime: {
          ...EMPTY_ANALYTICS.weekly.purchaseTypes.oneTime,
          ...(weekly.purchaseTypes?.oneTime || {})
        }
      },
      entryPoints: {
        ...EMPTY_ANALYTICS.weekly.entryPoints,
        ...(weekly.entryPoints || {})
      },
      purchaseSeries: {
        ...EMPTY_PURCHASE_SERIES,
        ...(weekly.purchaseSeries || {}),
        series: (() => {
          if (!Array.isArray(weekly.purchaseSeries?.series)) {
            return EMPTY_PURCHASE_SERIES.series.map((item) => ({ ...item }));
          }
          const incoming = new Map(
            weekly.purchaseSeries.series.map((item) => [item?.key || "", item || {}])
          );
          return EMPTY_PURCHASE_SERIES.series.map((item) => ({
            ...item,
            ...(incoming.get(item.key) || {}),
            data: Array.isArray(incoming.get(item.key)?.data)
              ? incoming.get(item.key).data
              : []
          }));
        })()
      },
      roles: {
        ...EMPTY_ANALYTICS.weekly.roles,
        ...(weekly.roles || {})
      },
      oneTimeToSubscription: {
        ...EMPTY_ANALYTICS.weekly.oneTimeToSubscription,
        ...(weekly.oneTimeToSubscription || {})
      },
      subscriptions: {
        ...EMPTY_ANALYTICS.weekly.subscriptions,
        ...(weekly.subscriptions || {}),
        byTariff: Array.isArray(weekly.subscriptions?.byTariff) ? weekly.subscriptions.byTariff : []
      }
    },
    requests: {
      ...EMPTY_ANALYTICS.requests,
      ...requests,
      day: { ...EMPTY_REQUESTS_PERIOD, ...(requests.day || {}) },
      week: { ...EMPTY_REQUESTS_PERIOD, ...(requests.week || {}) },
      month: { ...EMPTY_REQUESTS_PERIOD, ...(requests.month || {}) },
      allTime: { ...EMPTY_REQUESTS_PERIOD, ...(requests.allTime || {}) }
    },
    cohorts: {
      ...EMPTY_ANALYTICS.cohorts,
      ...cohorts
    },
    retention: {
      ...EMPTY_ANALYTICS.retention,
      ...retention
    },
    dropOff: {
      ...EMPTY_ANALYTICS.dropOff,
      ...dropOff
    },
    conclusions: {
      ...EMPTY_ANALYTICS.conclusions,
      ...conclusions
    },
    dashboard: {
      ...EMPTY_ANALYTICS.dashboard,
      ...dashboard
    }
  };
}

export function useAdminAnalytics({ adminToken, enabled, autoRefresh = true }) {
  const [stats, setStats] = useState(INITIAL_STATS);
  const [analyticsData, setAnalyticsData] = useState({ ...EMPTY_ANALYTICS });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const analyticsRefreshTimer = useRef(null);

  const fetchStats = useCallback(async (token = adminToken) => {
    if (!token) return;
    try {
      const res = await adminApi.getStats(token);
      setStats(res.data || INITIAL_STATS);
    } catch (error) {
      console.error(error);
    }
  }, [adminToken]);

  const fetchAnalytics = useCallback(async (token = adminToken, force = false) => {
    if (!token) return;
    setAnalyticsLoading(true);
    try {
      const res = await adminApi.getAnalytics(token, force);
      setAnalyticsData(normalizeAnalytics(res.data || {}));
    } catch (error) {
      console.error("Ошибка загрузки аналитики:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (!enabled || !adminToken) return;
    fetchStats(adminToken);
    fetchAnalytics(adminToken);
  }, [enabled, adminToken, fetchStats, fetchAnalytics]);

  useEffect(() => {
    if (!enabled || !adminToken || !autoRefresh || !analyticsData.refreshing) return undefined;

    if (analyticsRefreshTimer.current) {
      clearTimeout(analyticsRefreshTimer.current);
    }

    analyticsRefreshTimer.current = setTimeout(() => {
      fetchAnalytics(adminToken);
    }, 2000);

    return () => {
      if (analyticsRefreshTimer.current) {
        clearTimeout(analyticsRefreshTimer.current);
        analyticsRefreshTimer.current = null;
      }
    };
  }, [enabled, adminToken, autoRefresh, analyticsData.refreshing, fetchAnalytics]);

  return {
    data: {
      stats,
      analytics: analyticsData
    },
    loading: analyticsLoading,
    actions: {
      refresh: () => fetchAnalytics(adminToken, true),
      refetchStats: () => fetchStats(adminToken),
      refetchAnalytics: () => fetchAnalytics(adminToken)
    }
  };
}

export default useAdminAnalytics;
